import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProjectDiscussions, createProjectDiscussion, updateProjectDiscussion, deleteProjectDiscussion } from '@services/projects.service';
import { getRealtimeSocket, subscribeProject, unsubscribeProject } from '@services/realtime.socket';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_DISCUSSION_VIEW, DISCUSSION_SOCKET_EVENTS } from './constants/discussion.constants';
import { PROJECT_DISCUSSIONS_QUERY_KEY } from './constants/discussion.queryKeys';
import { getProjectIdFromDiscussionSocketPayload, getPostFromDiscussionSocketPayload } from './utils/discussionSocketPayload.utils';
import { sortByNewestFirst } from './utils/discussionSort.utils';

// payload parsing helpers live in utils/

/**
 * Discussion tab: list of posts, create post. Subscribes to project room and appends
 * new posts from project:discussion:new (one-time per event, no refetch).
 */
export function useProjectDiscussions(projectId) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canView = can(PROJECT_DISCUSSION_VIEW);

    useEffect(() => {
        if (!projectId || !canView) return;
        const socket = getRealtimeSocket();
        if (!socket) return;

        const handleNewPost = (data) => {
            const targetProjectId = getProjectIdFromDiscussionSocketPayload(data);
            if (targetProjectId == null || String(targetProjectId) !== String(projectId)) return;
            const post = getPostFromDiscussionSocketPayload(data);
            if (!post) return;
            queryClient.setQueryData(PROJECT_DISCUSSIONS_QUERY_KEY(projectId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                const id = post.id ?? post.discussionId;
                if (id != null && list.some((p) => String(p.id ?? p.discussionId) === String(id))) return prev;
                return sortByNewestFirst([...list, post]);
            });
        };

        const handleUpdatePost = (data) => {
            const targetProjectId = getProjectIdFromDiscussionSocketPayload(data);
            if (targetProjectId == null || String(targetProjectId) !== String(projectId)) return;
            const post = getPostFromDiscussionSocketPayload(data);
            if (!post) return;
            const id = post.id ?? post.discussionId;
            queryClient.setQueryData(PROJECT_DISCUSSIONS_QUERY_KEY(projectId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.map((p) => (String(p.id ?? p.discussionId) === String(id) ? { ...p, ...post } : p));
            });
        };

        const handleDeletePost = (data) => {
            const targetProjectId = getProjectIdFromDiscussionSocketPayload(data);
            if (targetProjectId == null || String(targetProjectId) !== String(projectId)) return;
            const id = data.messageId ?? data.id;
            if (id == null) return;
            queryClient.setQueryData(PROJECT_DISCUSSIONS_QUERY_KEY(projectId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.filter((p) => String(p.id ?? p.discussionId) !== String(id));
            });
        };

        socket.on(DISCUSSION_SOCKET_EVENTS.PROJECT_NEW, handleNewPost);
        socket.on(DISCUSSION_SOCKET_EVENTS.PROJECT_UPDATED, handleUpdatePost);
        socket.on(DISCUSSION_SOCKET_EVENTS.PROJECT_DELETED, handleDeletePost);
        subscribeProject(projectId);

        return () => {
            socket.off(DISCUSSION_SOCKET_EVENTS.PROJECT_NEW, handleNewPost);
            socket.off(DISCUSSION_SOCKET_EVENTS.PROJECT_UPDATED, handleUpdatePost);
            socket.off(DISCUSSION_SOCKET_EVENTS.PROJECT_DELETED, handleDeletePost);
            unsubscribeProject(projectId);
        };
    }, [projectId, queryClient, canView]);

    const {
        data: discussions = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_DISCUSSIONS_QUERY_KEY(projectId),
        queryFn: () => getProjectDiscussions(projectId),
        enabled: !!projectId && canView,
        // Discussion is kept fresh by sockets; avoid refetching every time panel/tab remounts.
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        // Fetch on first open and when stale; skip noisy remount refetches while fresh.
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const discussionsNewestFirst = useMemo(
        () => sortByNewestFirst(Array.isArray(discussions) ? discussions : []),
        [discussions],
    );

    const createMutation = useMutation({
        mutationFn: (payload) => createProjectDiscussion(projectId, payload),
        onSuccess: (data) => {
            const post = data?.data ?? data;
            if (post && (post.id != null || post.discussionId != null)) {
                queryClient.setQueryData(PROJECT_DISCUSSIONS_QUERY_KEY(projectId), (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    const id = post.id ?? post.discussionId;
                    if (list.some((p) => (p.id ?? p.discussionId) === id)) return prev;
                    return sortByNewestFirst([...list, post]);
                });
            }
        },
    });

    const createPost = (body, callbacks = {}) => {
        const { content, attachments = [], files } = body;
        const linkAttachments = Array.isArray(attachments) ? attachments : [];
        let payload;
        if (files?.length) {
            const formData = new FormData();
            formData.append('content', typeof content === 'string' ? content : '');
            if (linkAttachments.length > 0) {
                formData.append('attachmentUrls', JSON.stringify(linkAttachments));
            }
            files.forEach((file) => formData.append('files', file));
            payload = formData;
        } else {
            payload = {
                content: typeof content === 'string' ? content : '',
                attachments: linkAttachments,
            };
        }
        createMutation.mutate(payload, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const editMutation = useMutation({
        mutationFn: ({ discussionId, payload }) => updateProjectDiscussion(projectId, discussionId, payload),
        onSuccess: (data, { discussionId }) => {
            const post = data?.data ?? data;
            queryClient.setQueryData(PROJECT_DISCUSSIONS_QUERY_KEY(projectId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.map((p) => ((p.id ?? p.discussionId) === discussionId ? { ...p, ...post } : p));
            });
        },
    });

    const editPost = (discussionId, body, callbacks = {}) => {
        const { content, attachments = [], files } = body;
        const linkAttachments = Array.isArray(attachments) ? attachments : [];
        let payload;
        if (files?.length) {
            const formData = new FormData();
            formData.append('content', typeof content === 'string' ? content : '');
            if (linkAttachments.length > 0) {
                formData.append('attachmentUrls', JSON.stringify(linkAttachments));
            }
            files.forEach((file) => formData.append('files', file));
            payload = formData;
        } else {
            payload = {
                content: typeof content === 'string' ? content : '',
                attachments: linkAttachments,
            };
        }
        editMutation.mutate({ discussionId, payload }, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const deleteMutation = useMutation({
        mutationFn: (discussionId) => deleteProjectDiscussion(projectId, discussionId),
        onSuccess: (_, discussionId) => {
            queryClient.setQueryData(PROJECT_DISCUSSIONS_QUERY_KEY(projectId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.filter((p) => (p.id ?? p.discussionId) !== discussionId);
            });
        },
    });

    const deletePost = (discussionId, callbacks = {}) => {
        deleteMutation.mutate(discussionId, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };


    return {
        discussions: discussionsNewestFirst,
        isLoading,
        isError,
        refetch,
        createPost,
        createPostPending: createMutation.isPending,
        editPost,
        editPostPending: editMutation.isPending,
        deletePost,
        deletePostPending: deleteMutation.isPending,
    };
}


